// frontend/src/utils/execution/processPendingExecution.js

import { buildConfirmedRelationshipWritePayload } from '../intents/relationshipIntentUtils';
import {
  isAffirmativeConfirmation,
  isNegativeConfirmation,
} from '../conversation/pendingQuestionResponses';
import { applyCorrection } from '../corrections/relationshipCorrection';
import { upsertRelationshipState } from '../memory/mongoMemoryUtils';
import { validateExecution } from './executionValidator';
import { debugLog } from '../logging/debugLog';
import {
  getScopedCancellationContinuation,
  isPendingExecutionExpired,
} from './pendingExecutionLifecycle';

export async function processPendingExecution({
  message,
  memory,
  currentRelationships,
  currentRelationshipNetwork,
  effectiveUserName,
  updateMemory,
  setLastExecutionDecision,
  appendMessagePair,
  patchRelationshipStates,
  patchRelationshipNetworkEdges,
}) {
  if (memory.pendingWriteConfirmation) {
    const pendingWrite = memory.pendingWriteConfirmation;
    const pendingExpired = isPendingExecutionExpired(pendingWrite);

    if (pendingExpired) {
      updateMemory('pendingWriteConfirmation', null);
      updateMemory('pendingQuestion', null);

      setLastExecutionDecision({
        decision: 'IDLE',
        displayDecision: 'IDLE',
        intent: 'relationship_add',
        category: 'memory',
        operation: 'write',
        target: pendingWrite.personName || '—',
        reason: 'Pending relationship write expired without authority.',
      });
    } else if (isAffirmativeConfirmation(message)) {
      const executionCheck = validateExecution({
        intent: { type: 'relationship_add' },
        targetEntity: {
          name: pendingWrite.personName || null,
        },
        relationshipState: currentRelationships,
        message,
        conversationState: null,
      });

      setLastExecutionDecision({
        ...executionCheck,
        displayDecision: executionCheck.decision,
      });

      if (executionCheck.decision === 'BLOCK') {
        appendMessagePair(
          message,
          "I’m not able to save that relationship right now."
        );

        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }

      if (executionCheck.decision === 'CONFIRM') {
        appendMessagePair(
          message,
          `Just to confirm — should I remember ${pendingWrite.personName} as your ${pendingWrite.relationship}?`
        );

        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }

      try {
        const structuredRelationshipWrite = buildConfirmedRelationshipWritePayload(
          pendingWrite.intentInfo || pendingWrite
        );

        if (!structuredRelationshipWrite) {
          throw new Error('Missing structured relationship write payload');
        }

        const relationshipWriteResult = await upsertRelationshipState({
          ...structuredRelationshipWrite,
          includeGraphResult: true,
        });
        const upsertedRelationship = relationshipWriteResult?.relationship || null;
        const graphEdge = relationshipWriteResult?.graphEdge || null;

        if (!upsertedRelationship) {
          throw new Error('Relationship state upsert returned no canonical record');
        }

        currentRelationships = typeof patchRelationshipStates === 'function'
          ? patchRelationshipStates(upsertedRelationship)
          : currentRelationships;

        if (graphEdge && typeof patchRelationshipNetworkEdges === 'function') {
          currentRelationshipNetwork = patchRelationshipNetworkEdges(graphEdge);
        }

        updateMemory('pendingWriteConfirmation', null);
        updateMemory('pendingQuestion', null);

        setLastExecutionDecision({
          ...executionCheck,
          displayDecision: 'COMPLETED',
          reason: `Confirmed relationship write completed for ${pendingWrite.personName}.`,
        });

        appendMessagePair(
          message,
          `Okay, I’ll remember ${pendingWrite.personName} as your ${pendingWrite.relationship}.`
        );
      } catch (err) {
        console.error('❌ Failed to confirm relationship write:', err.message);

        setLastExecutionDecision({
          decision: 'BLOCK',
          displayDecision: 'BLOCK',
          intent: 'relationship_add',
          category: 'memory',
          operation: 'write',
          target: pendingWrite.personName || '—',
          reason: `Relationship write failed for ${pendingWrite.personName}.`,
        });

        appendMessagePair(
          message,
          `I’m sorry — I had trouble saving ${pendingWrite.personName} as your ${pendingWrite.relationship}.`
        );
      }

      return {
        handled: true,
        message,
        currentRelationships,
        currentRelationshipNetwork,
      };
    } else if (isNegativeConfirmation(message) || getScopedCancellationContinuation(message)) {
      const continuationMessage = getScopedCancellationContinuation(message);

      updateMemory('pendingWriteConfirmation', null);
      updateMemory('pendingQuestion', null);

      setLastExecutionDecision({
        decision: 'CANCELLED',
        displayDecision: 'CANCELLED',
        intent: 'relationship_add',
        category: 'memory',
        operation: 'write',
        target: pendingWrite.personName || '—',
        reason: `Relationship write cancelled for ${pendingWrite.personName}.`,
      });

      appendMessagePair(
        message,
        `Okay, I won’t save ${pendingWrite.personName} as your ${pendingWrite.relationship}.`
      );

      if (continuationMessage) {
        message = continuationMessage;
      } else {
        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }
    } else {
      setLastExecutionDecision({
        decision: 'CONFIRM',
        displayDecision: 'CONFIRM',
        intent: 'relationship_add',
        category: 'memory',
        operation: 'write',
        target: pendingWrite.personName || '—',
        reason: 'Pending memory edit requires explicit yes or no authority.',
      });

      appendMessagePair(
        message,
        `${effectiveUserName}, I need a specific "yes" or "no" before I edit memory.`
      );

      return {
        handled: true,
        message,
        currentRelationships,
        currentRelationshipNetwork,
      };
    }
  }

  if (memory.pendingCorrection) {
    const pendingCorrection = memory.pendingCorrection;
    const pendingExpired = isPendingExecutionExpired(pendingCorrection);

    if (pendingExpired) {
      updateMemory('pendingCorrection', null);
      updateMemory('pendingQuestion', null);

      setLastExecutionDecision({
        decision: 'IDLE',
        displayDecision: 'IDLE',
        intent: 'relationship_remove',
        category: 'memory',
        operation: 'delete',
        target: pendingCorrection.name || '—',
        reason: 'Pending relationship removal expired without authority.',
      });
    } else if (isAffirmativeConfirmation(message)) {
      const executionCheck = validateExecution({
        intent: { type: 'relationship_remove' },
        targetEntity: {
          name: pendingCorrection.name || null,
        },
        relationshipState: currentRelationships,
        message,
        conversationState: null,
      });

      setLastExecutionDecision({
        ...executionCheck,
        displayDecision: executionCheck.decision,
      });

      if (executionCheck.decision === 'BLOCK') {
        appendMessagePair(
          message,
          "I’m not able to remove that relationship right now."
        );

        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }

      if (executionCheck.decision === 'CONFIRM') {
        appendMessagePair(
          message,
          `Just to confirm — should I remove ${pendingCorrection.name} as your ${pendingCorrection.role}?`
        );

        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }

      const result = await applyCorrection(pendingCorrection);

      if (result?.success && typeof patchRelationshipStates === 'function') {
        currentRelationships = patchRelationshipStates({
          first_name: result.name,
          relationship: result.role,
          isActive: false,
          source: 'correction',
        });

        debugLog(
          '🧑‍🤝‍🧑 Relationships patched locally after correction:',
          currentRelationships
        );
      }

      if (
        result?.success &&
        result?.graphEdge &&
        typeof patchRelationshipNetworkEdges === 'function'
      ) {
        currentRelationshipNetwork = patchRelationshipNetworkEdges(result.graphEdge);

        debugLog(
          '🕸️ Relationship network patched locally after correction:',
          currentRelationshipNetwork
        );
      }

      updateMemory('pendingCorrection', null);
      updateMemory('pendingQuestion', null);

      setLastExecutionDecision({
        ...executionCheck,
        displayDecision: 'COMPLETED',
        reason: `Confirmed relationship removal completed for ${result.name}.`,
      });

      appendMessagePair(
        message,
        `Okay, I’ve removed ${result.name} as your ${result.role}.`
      );

      return {
        handled: true,
        message,
        currentRelationships,
        currentRelationshipNetwork,
      };
    } else if (isNegativeConfirmation(message) || getScopedCancellationContinuation(message)) {
      const continuationMessage = getScopedCancellationContinuation(message);

      setLastExecutionDecision({
        decision: 'CANCELLED',
        displayDecision: 'CANCELLED',
        intent: 'relationship_remove',
        category: 'memory',
        operation: 'delete',
        target: pendingCorrection.name || '—',
        reason: `Relationship removal cancelled for ${pendingCorrection.name}.`,
      });

      appendMessagePair(
        message,
        `Okay, I’ll keep ${pendingCorrection.name} as your ${pendingCorrection.role}.`
      );

      updateMemory('pendingCorrection', null);
      updateMemory('pendingQuestion', null);

      if (continuationMessage) {
        message = continuationMessage;
      } else {
        return {
          handled: true,
          message,
          currentRelationships,
          currentRelationshipNetwork,
        };
      }
    } else {
      setLastExecutionDecision({
        decision: 'CONFIRM',
        displayDecision: 'CONFIRM',
        intent: 'relationship_remove',
        category: 'memory',
        operation: 'delete',
        target: pendingCorrection.name || '—',
        reason: 'Pending memory edit requires explicit yes or no authority.',
      });

      appendMessagePair(
        message,
        `${effectiveUserName}, I need a specific "yes" or "no" before I edit memory.`
      );

      return {
        handled: true,
        message,
        currentRelationships,
        currentRelationshipNetwork,
      };
    }
  }

  return {
    handled: false,
    message,
    currentRelationships,
    currentRelationshipNetwork,
  };
}
